import { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Buttons, Content } from './styles';
import { EMPTY_STATE_SIZE, TAB_BUTTON_TYPE } from '../../global/constants';
import { ChartDataInterface } from '../../interfaces/TimeReportInterface';
import Tabs from '../tabs';
import { chartTabFilterOption } from '../../global/row';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../emptyState';

interface Props {
  projectWiseChartData: ChartDataInterface;
  onChangeChartType?: (value: number) => void;
  isOverviewPage?: boolean;
}

const Chart = ({ projectWiseChartData, onChangeChartType, isOverviewPage }: Props) => {
  const [chartData, setChartData] = useState<Highcharts.SeriesOptionsType[]>([]);
  // mobx store variable
  const { reportStore } = rootStore;
  const { reportFilter } = reportStore || {};

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: 'column',
        borderRadius: 8,
        marginTop: 18,
        marginRight: 24,
        marginLeft: 60,
        marginBottom: 55
      },
      xAxis: {
        categories: projectWiseChartData?.x_axis_data,
        title: {
          text: null
        },
        labels: {
          style: {
            color: 'var(--text-secondary)',
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '16px',
            fontFamily: 'Inter, sans-serif'
          }
        }
      },
      yAxis: {
        title: {
          text: ''
        },
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = this.value as number;
            const hours = Math.floor(value / 60);
            const minutes = value % 60;
            return `${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
          },
          style: {
            color: 'var(--text-secondary)',
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '16px',
            fontFamily: 'Inter, sans-serif'
          }
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      series: chartData,
      tooltip: {
        backgroundColor: 'var(--text-primary)',
        borderRadius: 2,
        style: {
          fontFamily: 'Inter, sans-serif',
          color: 'var(--background-primary)',
          fontSize: '12px',
          fontWeight: '500',
          lineHeight: '16px'
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `<b>${this.series.name}</b>: ${Math.floor((this.y || 0) / 60)}h ${(this.y || 0) % 60}m<br/>`;
        }
      }
    }),
    [chartData, projectWiseChartData?.x_axis_data]
  );

  useEffect(() => {
    if (projectWiseChartData?.data) {
      // Explicitly type the mapped array to match Highcharts column series
      const formattedData: Highcharts.SeriesColumnOptions[] = projectWiseChartData?.data?.map((item) => ({
        type: 'column',
        name: item.name,
        data: item.data.map((time) => {
          const [hours, minutes] = time.split(' : ');
          return parseInt(hours) * 60 + parseInt(minutes);
        })
      }));
      setChartData(formattedData);
    }
  }, [projectWiseChartData]);

  const renderChart = useMemo(() => {
    return (
      <div id='chartContainer'>
        <HighchartsReact highcharts={Highcharts} options={options} />{' '}
      </div>
    );
  }, [options]);

  // renders Tabs Button
  const renderTabButton = useCallback(() => {
    return chartTabFilterOption?.map((tab) => {
      return (
        <Tabs
          key={tab?.key}
          variant={TAB_BUTTON_TYPE.STANDARD}
          title={tab?.label}
          size='medium'
          onClick={() => onChangeChartType?.(tab?.key)}
          isActive={reportFilter?.chartType === tab?.key}
        />
      );
    });
  }, [onChangeChartType, reportFilter?.chartType]);

  return (
    <Content isOverviewPage={isOverviewPage}>
      <Buttons isOverviewPage={isOverviewPage}>{renderTabButton()}</Buttons>
      {chartData.length === 0
        ? !isOverviewPage && (
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.SMALL}
              title={`You don't have reports.`}
              description='There are no report data for this date. Please try another Filters.'
              imageUrl={'reports_empty'}
              className='reports-time-empty'
            />
          )
        : renderChart}
    </Content>
  );
};

export default observer(Chart);
